/* Names written in any of the app's scripts, compared by how they sound.
 *
 * A customer reading the app in Telugu types "విజయవాడ"; the cars, the city list and
 * Google's answers say "Vijayawada". Compared letter by letter those share nothing, so
 * the city was not found and the list came back empty.
 *
 * Telugu, Hindi (Devanagari), Tamil, Kannada - and Malayalam - share one layout: every
 * letter sits at the same place in its Unicode block (KA is 0x15 into each). So one
 * table, read by a letter's offset, reads all of them, and English goes through the
 * same classes. The key keeps only consonants, grouped so that spelling habits and
 * scripts do not matter:
 *   k g q            -> k      (Tamil writes k and g with one letter)
 *   c ch j z s sh    -> s      (Tamil ச is "s" in Visakhapatnam, "ch" in Chennai)
 *   t th d dh ṭ ḍ    -> t
 *   p ph b bh f      -> p
 *   n m ṇ ñ ṅ ం      -> n
 *   v w              -> v
 *   r ṟ, l ḷ ḻ       -> r, l
 *   h                -> h as a name's first letter ("Hyderabad") or between two vowels
 *                       ("Zaheerabad", "Mahabubnagar"); elsewhere it is an aspiration
 *                       ("Rajahmundry", "Visakhapatnam") and is dropped
 *   vowels and y     -> dropped, except that a name starting with one is marked "a"
 *                       ("Adoni" is not "Tuni")
 * A "c" before e, i or y is an s ("Siricilla"); elsewhere a k ("Cuddapah"). Doubled
 * letters count once. So "Vijayawada", "vijayavada", "విజయవాడ", "विजयवाड़ा", "விஜயவாடா"
 * and "ವಿಜಯವಾಡ" all read "vsvt". Words keep their own keys, joined by a space. */

// Offsets within a script block (the Devanagari layout), by class.
const OFFSET = {};
const put = (cls, ...offs) => offs.forEach((o) => { OFFSET[o] = cls; });
put("n", 0x01, 0x02, 0x19, 0x1e, 0x23, 0x28, 0x29, 0x2e);
put("k", 0x15, 0x16, 0x17, 0x18, 0x58, 0x59, 0x5a);
put("s", 0x1a, 0x1b, 0x1c, 0x1d, 0x5b, 0x36, 0x37, 0x38);
put("t", 0x1f, 0x20, 0x21, 0x22, 0x24, 0x25, 0x26, 0x27, 0x5c, 0x5d);
put("p", 0x2a, 0x2b, 0x2c, 0x2d, 0x5e);
put("r", 0x30, 0x31);
put("l", 0x32, 0x33, 0x34);
put("v", 0x35);
put("h", 0x39);

// Telugu puts other letters at three of those places: ౘ tsa, ౙ dza, ౚ rrra.
const TELUGU = 0x0c00;
const TELUGU_OWN = { 0x58: "s", 0x59: "s", 0x5a: "r" };

// Devanagari to Malayalam, the scripts that share the layout.
const FIRST = 0x0900, LAST = 0x0d7f;
export const INDIC = /[\u0900-\u0d7f]/;

const LATIN = { b: "p", p: "p", f: "p", c: "s", j: "s", z: "s", s: "s", k: "k", g: "k", q: "k", x: "ks",
  t: "t", d: "t", v: "v", w: "v", l: "l", r: "r", m: "n", n: "n", h: "h" };

// One of each run of the same letter.
const collapse = (k) => [...k].filter((ch, i, all) => ch !== all[i - 1]).join("");

// Within a block: the independent vowels, the vowel signs and the virama.
const isVowel = (off) => (off >= 0x04 && off <= 0x14) || off === 0x60 || off === 0x61;
const isSign = (off) => (off >= 0x3e && off <= 0x4c) || off === 0x62 || off === 0x63;
const VIRAMA = 0x4d, YA = 0x2f;

function indicWord(word) {
  const letters = [...word].map((ch) => ch.codePointAt(0)).filter((cp) => cp >= FIRST && cp <= LAST)
    .map((cp) => ({ block: cp - (cp % 0x80), off: cp % 0x80 }));
  if (!letters.length) return "";
  let out = isVowel(letters[0].off) || letters[0].off === YA ? "a" : "";
  letters.forEach(({ block, off }, i) => {
    let cls = (block === TELUGU && TELUGU_OWN[off]) || OFFSET[off] || "";
    if (cls === "h" && i > 0) {
      // A consonant carries its own vowel unless a virama follows it.
      const prev = letters[i - 1].off, next = letters[i + 1]?.off;
      const vowelBefore = isVowel(prev) || isSign(prev) || !!OFFSET[prev] || prev === YA;
      const vowelAfter = next !== undefined && next !== VIRAMA;
      if (!(vowelBefore && vowelAfter)) cls = "";
    }
    out += cls;
  });
  return collapse(out);
}

const VOWELS = "aeiouy";
function latinWord(word) {
  let x = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!x) return "";
  const lead = VOWELS.includes(x[0]) ? "a" : "";
  // "ch" is one sound; "c" is an s before e, i or y ("Siricilla"), otherwise a k.
  x = x.replace(/ch/g, "C").replace(/c(?=[eiy])/g, "s").replace(/c/g, "k").replace(/C/g, "c");
  x = x.replace(/([kgjtdpbsz])h/g, "$1");
  // What is left of "h": the first letter, or a sound between two vowels.
  x = [...x].filter((ch, i) => ch !== "h" || i === 0 || (VOWELS.includes(x[i - 1]) && VOWELS.includes(x[i + 1] || "_"))).join("");
  return collapse(lead + [...x].map((ch) => LATIN[ch] || "").join(""));
}

/** The sound key of a name in any of the scripts: one key per word, joined by spaces. */
export function soundKey(text) {
  return String(text || "").split(/[\s,.\-/()]+/)
    .map((w) => (INDIC.test(w) ? indicWord(w) : latinWord(w)))
    .filter(Boolean).join(" ");
}

/* Two keys name the same thing: equal, or equal once a final nasal is set aside - the
   Telugu "-am" of అనంతపురం is not in "Anantapur". Needs three sounds either way, so
   two short words do not agree by accident. */
export function sameSound(a, b) {
  if (!a || !b) return false;
  if (a === b) return a.replace(/\s/g, "").length >= 2;
  const trim = (k) => k.replace(/n$/, "");
  return trim(a) === trim(b) && trim(a).replace(/\s/g, "").length >= 3;
}
